import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import heroVideo from '../../assets/videos/round_pond.mp4';
import emmaScout from '../../assets/videos/emma_scout.mp4';
import inferenceMontage from '../../assets/videos/inference_montage.gif';
import budbaseDashboard from '../../assets/images/budbase_dashboard.jpg';
import emmaRobot from '../../assets/images/robot2.jpeg';
import atvImage from '../../assets/images/atv.jpg';
import emmaLettuce from '../../assets/images/emma_lettuce.jpg';
import wavePhoto from '../../assets/images/Wave.jpg';
import grapeOutput from '../../assets/images/grape_output.jpg';
import emmaFieldPhoto from '../../assets/images/emma_field.jpg';
import { blogPosts } from '../../constants/newsData';

/* ─── Animated Counter ──────────────────────────────────────────── */
const AnimatedCounter = ({ target, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '0px' });

  useEffect(() => {
    if (!isInView) return;
    const numStr = target.toString().replace(/,/g, '');
    const num = parseInt(numStr);
    if (isNaN(num)) { setCount(target); return; }
    const duration = 1500;
    const steps = 40;
    const increment = num / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= num) { setCount(num); clearInterval(timer); }
      else { setCount(Math.floor(current)); }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isInView, target]);

  return (
    <span ref={ref}>
      {prefix}{typeof count === 'number' ? count.toLocaleString() : count}{suffix}
    </span>
  );
};

/* ─── Section Wrapper ───────────────────────────────────────────── */
const FadeInSection = ({ children, className = '', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ─── Line Reveal Bar ───────────────────────────────────────────── */
const LineReveal = ({ delay = 0, className = '' }) => (
  <motion.span
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    viewport={{ once: true, amount: 0.5 }}
    transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`line-reveal-bar ${className}`}
    style={{ display: 'block' }}
  />
);

/* ─── Pentagon Radar Chart ──────────────────────────────────────── */
const PentagonRadar = ({ data, color, maxValue = 4 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });
  const n = data.length;
  const cx = 110, cy = 110, r = 72;
  const labelR = r + 22;
  const gridLevels = [0.25, 0.5, 0.75, 1.0];

  const angleFor = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / n;

  const gridPolygon = (level) =>
    data.map((_, i) => {
      const a = angleFor(i);
      return `${cx + level * r * Math.cos(a)},${cy + level * r * Math.sin(a)}`;
    }).join(' ');

  const dataPolygon = data.map((d, i) => {
    const a = angleFor(i);
    const dist = (d.value / maxValue) * r;
    return `${cx + dist * Math.cos(a)},${cy + dist * Math.sin(a)}`;
  }).join(' ');

  const fillColor = color + '33';

  return (
    <svg ref={ref} viewBox="-25 -15 270 250" className="w-full h-full">
      {gridLevels.map((lvl, i) => (
        <polygon
          key={i}
          points={gridPolygon(lvl)}
          fill="none"
          stroke="#d1d5db"
          strokeWidth="1"
          strokeDasharray={i === gridLevels.length - 1 ? 'none' : '3,3'}
        />
      ))}
      {data.map((_, i) => {
        const a = angleFor(i);
        return (
          <line
            key={i}
            x1={cx} y1={cy}
            x2={cx + r * Math.cos(a)}
            y2={cy + r * Math.sin(a)}
            stroke="#d1d5db"
            strokeWidth="1"
          />
        );
      })}
      <motion.polygon
        points={dataPolygon}
        fill={fillColor}
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        initial={{ scale: 0, opacity: 0 }}
        animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      />
      {data.map((d, i) => {
        const a = angleFor(i);
        const cosA = Math.cos(a);
        const lx = cx + labelR * cosA;
        const ly = cy + labelR * Math.sin(a);
        const anchor = cosA > 0.1 ? 'start' : cosA < -0.1 ? 'end' : 'middle';
        const words = d.label.split(' ');
        const mid = Math.ceil(words.length / 2);
        const line1 = words.slice(0, mid).join(' ');
        const line2 = words.slice(mid).join(' ');
        return (
          <text key={i} x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle" fontSize="7.5" fill="#6b7280" fontFamily="Inter, sans-serif">
            {line2 ? (
              <>
                <tspan x={lx} dy="-5">{line1}</tspan>
                <tspan x={lx} dy="11">{line2}</tspan>
              </>
            ) : (
              line1
            )}
          </text>
        );
      })}
      <circle cx={cx} cy={cy} r="3" fill={color} opacity="0.5" />
    </svg>
  );
};

/* ─── Platform Slideshow ────────────────────────────────────────── */
const PlatformSlideshow = () => {
  const [active, setActive] = useState(0);
  const slides = [
    { src: emmaRobot, label: 'Autonomous Robot', alt: 'Emma, the autonomous crop scouting robot' },
    { src: atvImage, label: 'ATV Mounted', alt: 'ATV-mounted scouting solution' },
  ];
  return (
    <div className="space-y-2">
      <div className="rounded-2xl overflow-hidden shadow-2xl relative">
        {slides.map((slide, i) => (
          <img
            key={slide.label}
            src={slide.src}
            alt={slide.alt}
            className={`w-full h-48 sm:h-72 md:h-80 object-cover transition-opacity duration-500 ${i === active ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
          />
        ))}
      </div>
      <div className="flex justify-center gap-2 pt-1">
        {slides.map((slide, i) => (
          <button
            key={slide.label}
            onClick={() => setActive(i)}
            className={`px-3 py-1 rounded-full text-xs font-medium tracking-wide uppercase transition-colors duration-200 ${
              i === active
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/20'
            }`}
          >
            {slide.label}
          </button>
        ))}
      </div>
    </div>
  );
};

/* ─── Detection Card with Radar Chart ──────────────────────────── */
const DetectionCard = ({ item, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      className="relative p-8 rounded-2xl bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/5 card-lift group flex flex-col gap-4"
    >
      <div>
        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2 font-sans">
          {item.label}. {item.title}
        </h4>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: 0.9, delay: index * 0.12 + 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="line-reveal-bar mb-3"
        />
        <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
          {item.desc}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {item.items.map((it) => (
            <span
              key={it}
              className="px-2.5 py-1 rounded-full text-xs text-gray-500 dark:text-gray-400"
              style={{ backgroundColor: `${item.color}12`, border: `1px solid ${item.color}25` }}
            >
              {it}
            </span>
          ))}
        </div>
      </div>
      <div className="absolute inset-0 rounded-2xl bg-brand-500/0 group-hover:bg-brand-500/[0.02] transition-colors duration-500" />
    </motion.div>
  );
};

/* ─── Feature Card ──────────────────────────────────────────────── */
const FeatureCard = ({ feature, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="group relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-[380px] md:h-[420px] rounded-2xl overflow-hidden card-lift">
        <div className="absolute inset-0">
          {(feature.video.endsWith('.gif') || feature.video.endsWith('.jpg') || feature.video.endsWith('.jpeg') || feature.video.endsWith('.png')) ? (
            <img
              src={feature.video}
              alt={feature.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <video
              src={feature.video}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
        <div className="absolute top-5 left-5">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-500/90 text-white text-sm font-bold backdrop-blur-sm">
            {index + 1}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-7">
          <h3 className="text-xl md:text-2xl font-display text-white mb-2">
            {feature.title}
          </h3>
          <motion.p
            initial={false}
            animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 10 }}
            transition={{ duration: 0.3 }}
            className="text-gray-200 text-sm leading-relaxed"
          >
            {feature.desc}
          </motion.p>
        </div>
      </div>
    </motion.div>
  );
};

/* ─── Pillar Card ───────────────────────────────────────────────── */
const PillarCard = ({ pillar, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.7, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
    className={`relative flex flex-col p-8 rounded-2xl border card-lift overflow-hidden ${
      pillar.active
        ? 'bg-white dark:bg-white/[0.04] border-brand-200 dark:border-brand-800/40'
        : 'bg-white dark:bg-white/[0.02] border-gray-100 dark:border-white/5'
    }`}
  >
    {/* Status badge */}
    <div className="mb-5 flex items-center justify-between">
      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
          pillar.active
            ? 'bg-brand-500 shadow-lg shadow-brand-500/30'
            : 'bg-gray-100 dark:bg-white/5'
        }`}
      >
        <div className={pillar.active ? 'text-white' : 'text-gray-400 dark:text-gray-500'}>
          {pillar.icon}
        </div>
      </div>
      <span
        className={`text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full ${
          pillar.statusColor
        }`}
      >
        {pillar.status}
      </span>
    </div>

    <h3 className="text-2xl font-display font-bold text-gray-900 dark:text-white mb-3">
      {pillar.title}
    </h3>
    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-5 flex-1">
      {pillar.desc}
    </p>

    <ul className="space-y-2">
      {pillar.bullets.map((b) => (
        <li key={b} className="flex items-start gap-2.5">
          <div className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-0.5 ${
            pillar.active ? 'bg-brand-500/15' : 'bg-gray-100 dark:bg-white/5'
          }`}>
            <svg className={`w-2.5 h-2.5 ${pillar.active ? 'text-brand-600 dark:text-brand-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed">{b}</span>
        </li>
      ))}
    </ul>

    {/* Active glow */}
    {pillar.active && (
      <div className="absolute inset-0 rounded-2xl ring-1 ring-brand-500/20 pointer-events-none" />
    )}
  </motion.div>
);

/* ─── Main Home Component ───────────────────────────────────────── */
const Home = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 150]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.3]);

  const pillars = [
    {
      title: 'Perception',
      status: 'Deployed',
      statusColor: 'bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300',
      active: true,
      desc: 'Budbreak gives you complete per-plant visibility across your entire operation across any specialty crop. Emma drives your rows, captures every plant, and you get a full report within 24 hours.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      bullets: [
        'Emma covers rows at 2,500+ plants per hour',
        'Per-plant data: disease, growth stage, yield indicators',
        'Full report live in Budbase within 24 hours of the scan',
        'Deployed in wine grapes, blueberries, lettuce, walnuts, and more',
      ],
    },
    {
      title: 'Budbase',
      status: 'Deployed',
      statusColor: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
      active: true,
      desc: 'Your scan data lives in Budbase - organized by block, season, and crop. Filter by what matters, compare across years, and share action plans directly with your crew or agronomist.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8"
            d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
        </svg>
      ),
      bullets: [
        'Per-plant data organized by block, variety, and season',
        'Track disease trends and yield signals across multiple years',
        'Export spray maps and action plans for your crew',
        'Early access available now for 2026 partners',
      ],
    },
    {
      title: 'Actuation',
      status: 'Coming Soon',
      statusColor: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
      active: false,
      desc: 'Our robotic platform is being built from the ground up to not just look, but act. This includes targeted spraying, precision pruning, and assisted harvesting for specialty crops. Built on the same platform already running in your field.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      bullets: [
        'Targeted spraying: treat only the plants that need it',
        'Precision pruning and assisted harvesting on familiar hardware',
        'Every action guided by real scan data from your field',
        'Being co-developed with grower partners. Reach out to get involved',
      ],
    },
  ];

  const features = [
    {
      title: "We Scout Your Farm",
      desc: "Emma drives through your rows autonomously, capturing high-resolution images of every plant in real time.",
      video: emmaScout,
    },
    {
      title: "We Diagnose Your Crops",
      desc: "Our AI turns Emma's photos into a per-plant digital twin, so you can see exactly what's happening, plant by plant.",
      video: inferenceMontage,
    },
    {
      title: "We Provide Actionable Insights",
      desc: "Filter by block, flag priority plants, and export an action plan. Make decisions backed by data, not guesswork.",
      video: budbaseDashboard,
    },
  ];

  const stats = [
    { value: '2500', label: 'Plants scanned per hour', suffix: '+', animate: true },
    { displayText: '95%+', label: 'Detection accuracy' },
    { displayText: 'Per-plant', label: 'Level insights' },
  ];

  const detectionItems = [
    {
      label: 'A',
      title: 'Health & Growth Stages',
      desc: 'Understand canopy development, structural gaps, and nutritional status to guide sprays, fertigation, and replanting.',
      color: '#22c55e',
      items: ['E-L Growth Stages', 'Vine Mapping', 'Vigor Maps', 'Potash Deficiency', 'Other Nutrient Deficiencies'],
      chartData: [
        { label: 'E-L Growth Stages', value: 2 },
        { label: 'Vine Mapping', value: 4 },
        { label: 'Vigor Maps', value: 2 },
        { label: 'Potash Deficiency', value: 1 },
        { label: 'Other Nutrients', value: 2 },
      ],
    },
    {
      label: 'B',
      title: 'Diseases & Pests',
      desc: 'Earlier detection reduces crop loss and unnecessary sprays, and helps isolate infected areas for management.',
      color: '#f87171',
      items: ['Red Blotch', 'Leafroll', 'Powdery Mildew', 'Downy Mildew', 'Other Anomaly Detections'],
      chartData: [
        { label: 'Red Blotch', value: 4 },
        { label: 'Leafroll', value: 4 },
        { label: 'Powdery Mildew', value: 2 },
        { label: 'Downy Mildew', value: 3 },
        { label: 'Other Anomalies', value: 3 },
      ],
    },
    {
      label: 'C',
      title: 'Yield Estimation',
      desc: 'These metrics forecast crop load and inform thinning, irrigation, and labor planning.',
      color: '#a78bfa',
      items: ['Bud Count', 'Shoot Measurement', 'Cluster Count / Volume', 'Berry Count / Size', 'Yield Weight'],
      chartData: [
        { label: 'Bud Count', value: 2 },
        { label: 'Shoot Measurement', value: 2 },
        { label: 'Cluster Count/Volume', value: 2 },
        { label: 'Yield Weight (tons)', value: 1 },
        { label: 'Berry Count/Size', value: 2 },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1a09]">

      {/* ═══ HERO ═══ */}
      <section className="relative flex flex-col min-h-screen w-full overflow-hidden">
        {/* Video background */}
        <motion.div
          className="absolute inset-0 w-full h-full"
          style={{ y: heroY, opacity: heroOpacity }}
        >
          <video
            className="w-full h-full object-cover scale-110"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
        </motion.div>
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1a09]/75 via-[#0f1a09]/35 to-[#0f1a09]/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1a09]/55 via-transparent to-transparent" />

        {/* Hero content */}
        <div className="flex-1 flex flex-col justify-center items-center text-center z-10 relative px-6 pt-20">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl md:text-6xl lg:text-7xl font-display text-white mb-6 leading-[1.08] max-w-5xl"
          >
            The Robotics Platform{' '}
            <span className="italic text-white" style={{textShadow: '0 0 40px rgba(255,255,255,0.4), 0 0 12px rgba(255,255,255,0.2)'}}>
              for Agriculture
            </span>
            .
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.42 }}
            className="flex flex-col sm:flex-row gap-4 items-center"
          >
            <motion.button
              whileHover={{ scale: 1.04, boxShadow: '0 12px 40px rgba(34, 197, 94, 0.35)' }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/contact')}
              className="px-10 py-4 text-white bg-brand-500 hover:bg-brand-600 font-semibold text-base rounded-full transition-all duration-300 shadow-lg shadow-brand-500/30"
            >
              Get Your Free 5 Acre Scan
            </motion.button>
          </motion.div>
        </div>

        {/* ═══ STATS BAR ═══ */}
        <motion.div
          style={{ opacity: useTransform(scrollY, [0, 200], [0, 1]) }}
          className="absolute bottom-0 left-0 right-0 z-20 w-full"
        >
          <div className="glass-dark py-6 md:py-8 border-t border-white/5 bg-[#0f1a09]/40 backdrop-blur-md">
            <div className="max-w-5xl mx-auto px-6">
              <div className="grid grid-cols-3 gap-x-4 gap-y-0.5 md:gap-x-8 md:gap-y-1">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center text-2xl md:text-5xl font-bold text-white font-display tracking-tight">
                    {stat.animate ? (
                      <span className="bg-gradient-to-br from-white to-gray-400 bg-clip-text text-transparent">
                        <AnimatedCounter target={stat.value} suffix={stat.suffix} prefix={stat.prefix || ''} />
                      </span>
                    ) : (
                      <span className="bg-gradient-to-br from-white to-gray-400 bg-clip-text text-transparent">
                        {stat.displayText}
                      </span>
                    )}
                  </div>
                ))}
                {stats.map((stat) => (
                  <div key={stat.label + '-label'} className="text-center text-[10px] md:text-xs uppercase tracking-widest text-brand-400 font-semibold">
                    {stat.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </section>


      {/* ═══ INTRO TEXT ═══ */}
      <section className="py-16 md:py-24 lg:py-32 bg-white dark:bg-[#0f1a09]">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection>
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-display text-gray-900 dark:text-white mb-5 leading-tight">
                Helping farmers grow<br className="hidden md:block" /> the farm of the future.
              </h2>
              <LineReveal className="mx-auto w-16 mb-5" />
              <p className="text-gray-500 dark:text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
                Farming has always run on experience and intuition, but lacked full data. We're giving growers the tools to add data and robotics to that equation so the next generation of farms can be more profitable, more sustainable, and more resilient.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ═══ ROBOT & MULTI-CROP DEPLOYMENT ═══ */}
      <section className="py-16 md:py-24 lg:py-32 bg-gray-50 dark:bg-[#131e0d]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInSection>
              <h2 className="text-4xl md:text-5xl font-display text-gray-900 dark:text-white mb-6 leading-tight">
                Our platform<br className="hidden md:block" />
                <span className="text-gradient"> in the field</span>.
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed max-w-lg">
                Emma is already being deployed across wine grapes, blueberries, lettuce, and more. Every deployment teaches the robot and grows the dataset, all to help growers manage their farms with ease.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  'Deployed across Napa, Sonoma, Lodi, Finger Lakes, and more',
                  'Works in cloudy and sunny conditions, slopes, and various row widths',
                  'Minimal setup or infrastructure required on-farm',
                  'ATV-mounted or standalone autonomous ground robot',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-500/10 dark:bg-brand-500/20 flex items-center justify-center">
                      <svg className="w-3 h-3 text-brand-600 dark:text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700 dark:text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              {/* Crop pills */}
              <div className="flex flex-wrap gap-2">
                {['Wine Grapes', 'Blueberries', 'Lettuce', 'Apples', 'Pears', 'Walnuts', 'More coming soon'].map((crop) => (
                  <span
                    key={crop}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300"
                  >
                    {crop}
                  </span>
                ))}
              </div>
            </FadeInSection>

            <FadeInSection delay={0.2}>
              <div className="grid grid-cols-2 gap-4">
                {/* Slideshow */}
                <PlatformSlideshow />
                {/* Emma in the field */}
                <div className="space-y-2 sm:pt-8">
                  <div className="rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={emmaFieldPhoto}
                      alt="Emma robot scouting a vineyard"
                      className="w-full h-48 sm:h-72 md:h-80 object-cover"
                    />
                  </div>
                </div>
              </div>
            </FadeInSection>
          </div>

          {/* ─── What We Detect ─── */}
          <div className="mt-24">
            <FadeInSection>
              <div className="text-center mb-4">
                <h3 className="text-3xl md:text-4xl font-display text-gray-900 dark:text-white mb-3">
                  What We Detect
                </h3>
                <LineReveal className="mx-auto w-16 mb-4" />
                <p className="text-gray-500 dark:text-gray-400 text-sm max-w-xl mx-auto">
                  Every scan improves our AI models. The more scans our robot completes, the more accurate our detection capability becomes for you and your farm.
                </p>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.1}>
              <div className="rounded-2xl overflow-hidden shadow-2xl mt-8 mb-10">
                <img
                  src={grapeOutput}
                  alt="Grape cluster output detected by Budbreak AI"
                  className="w-full h-64 md:h-80 object-cover"
                  style={{ objectPosition: 'center 78%' }}
                />
              </div>
            </FadeInSection>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              {detectionItems.map((item, i) => (
                <DetectionCard key={item.title} item={item} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" className="py-16 md:py-24 lg:py-32 bg-white dark:bg-[#0f1a09]">
        <div className="max-w-7xl mx-auto px-6">

          <FadeInSection>
            <h3 className="text-3xl md:text-4xl font-display text-gray-900 dark:text-white mb-3 text-center">
              How It Works
            </h3>
            <LineReveal className="mx-auto w-16 mb-10" />
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, i) => (
              <FeatureCard key={feature.title} feature={feature} index={i} />
            ))}
          </div>

        </div>
      </section>

      {/* ═══ THE DATA FLYWHEEL ═══ */}
      <section className="py-16 md:py-24 lg:py-32 bg-gray-50 dark:bg-[#131e0d]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInSection>
              <h2 className="text-4xl md:text-5xl font-display text-gray-900 dark:text-white mb-6 leading-tight">
                Closing the Agriculture Loop.
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                You've always known your fields better than anyone. Now you'll know them better than ever with rapid insights from every scan, compounding into a clearer picture with every season.
              </p>
            </FadeInSection>

            <FadeInSection delay={0.2}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={wavePhoto}
                  alt="Budbreak founder with Emma robot in a vineyard"
                  className="w-full h-80 md:h-[520px] object-cover hover:scale-[1.02] transition-transform duration-700"
                />
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF ═══ */}
      <section className="py-16 md:py-24 lg:py-32 bg-white dark:bg-[#0f1a09]">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-display text-gray-900 dark:text-white mb-4">
                Testimonials
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Straight from the growers and specialists who've seen Emma work.
              </p>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            <FadeInSection>
              <div className="relative p-8 md:p-10 rounded-2xl bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/5 card-lift h-full flex flex-col">
                <svg className="w-8 h-8 text-brand-500/30 mb-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-gray-800 dark:text-gray-200 text-lg leading-relaxed mb-6 font-light flex-1">
                  "For us at Round Pond, innovations like Emma align beautifully with our mission to care for the land while crafting world-class wines. Technology doesn't replace the skill or intuition of farmers. It strengthens them."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-1 h-8 bg-brand-500 rounded-full" />
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white text-sm font-sans">Round Pond Estate</p>
                    <p className="text-gray-500 dark:text-gray-500 text-xs">Napa Valley, CA</p>
                  </div>
                </div>
              </div>
            </FadeInSection>

            <FadeInSection delay={0.15}>
              <div className="relative p-8 md:p-10 rounded-2xl bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/5 card-lift h-full flex flex-col">
                <svg className="w-8 h-8 text-brand-500/30 mb-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-gray-800 dark:text-gray-200 text-lg leading-relaxed mb-6 font-light flex-1">
                  "As a field pathologist who has been looking at vines for years, I was impressed that the unit was able to rate foliar disease with a less than 1% difference from my personal ratings."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-1 h-8 bg-brand-500 rounded-full" />
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white text-sm font-sans">Dave Combs</p>
                    <p className="text-gray-500 dark:text-gray-500 text-xs">Plant Pathology Specialist, Geneva, NY</p>
                  </div>
                </div>
              </div>
            </FadeInSection>
          </div>

        </div>
      </section>

      {/* ═══ RECENT COVERAGE ═══ */}
      <section id="news" className="py-16 md:py-24 lg:py-32 bg-gray-50 dark:bg-[#131e0d]">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection>
            <div className="text-center mb-16">
              <span className="inline-block px-3 py-1 mb-4 text-xs font-semibold tracking-[0.15em] uppercase text-brand-600 dark:text-brand-400 bg-brand-100 dark:bg-brand-900/30 rounded-full">
                In The Press
              </span>
              <h2 className="text-4xl md:text-5xl font-display text-gray-900 dark:text-white mb-4">
                Recent Coverage
              </h2>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {blogPosts.slice(0, 3).map((post, i) => (
              <FadeInSection key={post.id} delay={i * 0.1}>
                <div className="group rounded-2xl overflow-hidden bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/5 card-lift h-full flex flex-col">
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-brand-500 text-white">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="text-xs text-gray-500 dark:text-gray-500 mb-3 font-medium tracking-wide uppercase">
                      {post.date}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 line-clamp-2 font-sans leading-snug">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm line-clamp-3 flex-1">
                      {post.excerpt}
                    </p>
                    {post.url ? (
                      <a
                        href={post.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-brand-600 dark:text-brand-400 font-medium text-sm hover:text-brand-700 dark:hover:text-brand-300 transition-colors group/link"
                      >
                        Read Article
                        <svg className="w-4 h-4 ml-1 transition-transform group-hover/link:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </a>
                    ) : (
                      <button
                        onClick={() => navigate('/news')}
                        className="inline-flex items-center text-brand-600 dark:text-brand-400 font-medium text-sm hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
                      >
                        Read Article →
                      </button>
                    )}
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>

          <FadeInSection>
            <div className="text-center">
              <button
                onClick={() => navigate('/news')}
                className="inline-flex items-center gap-2 px-8 py-3 text-sm font-semibold text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 hover:bg-brand-100 dark:hover:bg-brand-900/30 rounded-full transition-all duration-300 border border-brand-200 dark:border-brand-800/50"
              >
                View All News
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-16 md:py-24 lg:py-32 bg-white dark:bg-[#0f1a09]">
        <div className="max-w-4xl mx-auto px-6">
          <FadeInSection>
            <div className="relative overflow-hidden p-6 sm:p-10 md:p-16 rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 text-center">
              <h2 className="text-3xl md:text-5xl font-display text-white mb-6">
                Build the future of farming with us.
              </h2>
              <p className="text-brand-100 text-lg mb-4 max-w-2xl mx-auto">
                We're looking for growers, partners, and investors who believe robotics will transform agriculture and want to be the first ones to benefit from this technology.
              </p>
              <p className="text-brand-200/70 mb-10 max-w-2xl mx-auto">
                Closed beta spots are limited. Reach out to explore a strategic partnership or a free deployment on your farm.
              </p>
              <div className="flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.04, boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate('/contact')}
                  className="px-6 sm:px-10 py-4 text-brand-700 bg-white hover:bg-gray-50 font-bold text-base rounded-full transition-all duration-300 shadow-xl"
                >
                  Get a Free 5-Acre Scan
                </motion.button>
              </div>
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/5 rounded-full" />
              <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-white/5 rounded-full" />
              <div className="absolute top-1/2 right-10 w-20 h-20 bg-white/5 rounded-full" />
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
};

export default Home;
